import { Icon, addCollection } from '@iconify/react';
import type { League } from '@h2h/shared';
import { cn } from '../../lib/cn';
import { notoSportCollection } from './notoSportIcons';

addCollection(notoSportCollection);

const NOTO_GLOBE = 'noto:globe-showing-europe-africa';

/** Glossy enamel flag tile variants — colours match the league-icons mockup. */
export type FlagEmblemKey =
  | 'england'
  | 'spain'
  | 'italy'
  | 'denmark'
  | 'france'
  | 'switzerland'
  | 'belgium'
  | 'netherlands'
  | 'uci'
  | 'world';

type Size = 'mini' | 'chip' | 'xs' | 'sm' | 'md' | 'lg';

const DIM: Record<Size, { box: number; radius: number }> = {
  mini: { box: 30, radius: 9 },
  chip: { box: 26, radius: 9 },
  xs: { box: 34, radius: 10 },
  sm: { box: 40, radius: 12 },
  md: { box: 46, radius: 14 },
  lg: { box: 52, radius: 16 }
};

const LEAGUE_FLAG: Record<string, FlagEmblemKey> = {
  'premier-league': 'england',
  'la-liga': 'spain',
  'serie-a': 'italy',
  superliga: 'denmark',
  'world-cup-2026': 'world'
};

const COUNTRY_FLAG: Record<string, FlagEmblemKey> = {
  england: 'england',
  spain: 'spain',
  italy: 'italy',
  denmark: 'denmark',
  france: 'france',
  switzerland: 'switzerland',
  belgium: 'belgium',
  netherlands: 'netherlands',
  world: 'world'
};

/** PCS race slug → host country flag (curated seed list in cycling-data-tool). */
const CYCLING_SLUG_FLAG: Record<string, FlagEmblemKey> = {
  'tour-de-france': 'france',
  'giro-d-italia': 'italy',
  'vuelta-a-espana': 'spain',
  'milano-sanremo': 'italy',
  'ronde-van-vlaanderen': 'belgium',
  'paris-roubaix': 'france',
  'liege-bastogne-liege': 'belgium',
  'il-lombardia': 'italy',
  'strade-bianche': 'italy',
  'gent-wevelgem': 'belgium',
  'e3-harelbeke': 'belgium',
  'dwars-door-vlaanderen': 'belgium',
  'amstel-gold-race': 'netherlands',
  'la-fleche-wallonne': 'belgium',
  'paris-nice': 'france',
  'tirreno-adriatico': 'italy',
  'volta-a-catalunya': 'spain',
  'itzulia-basque-country': 'spain',
  'criterium-du-dauphine': 'france',
  'tour-de-suisse': 'switzerland',
  'world-championship': 'uci'
};

function normalizeCountry(country: string | null | undefined): FlagEmblemKey | null {
  if (!country) return null;
  return COUNTRY_FLAG[country.trim().toLowerCase()] ?? null;
}

function cyclingSlugFromLeagueId(leagueId: string): string | null {
  const m = leagueId.match(/^cycling-(.+)-\d{4}$/);
  return m?.[1] ?? null;
}

/** Resolve which flag tile to show for a league or race. */
export function flagEmblemKey(
  leagueId: string,
  country?: string | null,
  slug?: string | null
): FlagEmblemKey {
  if (LEAGUE_FLAG[leagueId]) return LEAGUE_FLAG[leagueId];

  const fromSlug = slug ? CYCLING_SLUG_FLAG[slug] : null;
  if (fromSlug) return fromSlug;

  const fromLeagueSlug = cyclingSlugFromLeagueId(leagueId);
  if (fromLeagueSlug && CYCLING_SLUG_FLAG[fromLeagueSlug]) return CYCLING_SLUG_FLAG[fromLeagueSlug];

  const fromCountry = normalizeCountry(country);
  if (fromCountry) return fromCountry;

  return 'world';
}

export function flagEmblemKeyForLeague(league: Pick<League, 'leagueId' | 'country' | 'cycling'>): FlagEmblemKey {
  return flagEmblemKey(league.leagueId, league.country, league.cycling?.slug);
}

function FlagArt({ flag, box }: { flag: FlagEmblemKey; box: number }) {
  if (flag === 'uci') {
    return (
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg,#0066CC 0 20%,#E4002B 20% 40%,#1A1A1A 40% 60%,#FFD200 60% 80%,#009639 80% 100%)'
        }}
      />
    );
  }

  if (flag === 'world') {
    return (
      <>
        {/* Deep ocean base — darker than the emoji so the globe reads forward. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 130% 115% at 32% 18%, #3D9AE0 0%, #1A6BB8 24%, #0C4F94 52%, #06356B 78%, #021A38 100%)'
          }}
        />
        {/* Cool vignette + warm WC highlight for depth. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 78% 88%, rgba(2,18,40,0.55), transparent 58%), radial-gradient(circle at 88% 12%, rgba(240,198,103,0.22), transparent 42%)'
          }}
        />
        <div className="absolute inset-0 grid place-items-center drop-shadow-[0_3px_6px_rgba(0,20,50,0.45)]">
          <Icon icon={NOTO_GLOBE} width={box * 0.72} height={box * 0.72} />
        </div>
      </>
    );
  }

  return (
    <svg viewBox="0 0 60 60" className="absolute inset-0 h-full w-full" aria-hidden>
      {flag === 'england' && (
        <>
          <rect width="60" height="60" fill="#fff" />
          <rect x="24" width="12" height="60" fill="#D7232E" />
          <rect y="24" width="60" height="12" fill="#D7232E" />
        </>
      )}
      {flag === 'spain' && (
        <>
          <rect width="60" height="60" fill="#C60B1E" />
          <rect y="15" width="60" height="30" fill="#FFC400" />
        </>
      )}
      {flag === 'italy' && (
        <>
          <rect width="20" height="60" fill="#008C45" />
          <rect x="20" width="20" height="60" fill="#fff" />
          <rect x="40" width="20" height="60" fill="#CD212A" />
        </>
      )}
      {flag === 'denmark' && (
        <>
          <rect width="60" height="60" fill="#C8102E" />
          <rect x="18" width="10" height="60" fill="#fff" />
          <rect y="25" width="60" height="10" fill="#fff" />
        </>
      )}
      {flag === 'france' && (
        <>
          <rect width="20" height="60" fill="#0055A4" />
          <rect x="20" width="20" height="60" fill="#fff" />
          <rect x="40" width="20" height="60" fill="#EF4135" />
        </>
      )}
      {flag === 'switzerland' && (
        <>
          <rect width="60" height="60" fill="#D52B1E" />
          <rect x="26" y="14" width="8" height="32" fill="#fff" />
          <rect x="14" y="26" width="32" height="8" fill="#fff" />
        </>
      )}
      {flag === 'belgium' && (
        <>
          <rect width="20" height="60" fill="#000" />
          <rect x="20" width="20" height="60" fill="#FDDA24" />
          <rect x="40" width="20" height="60" fill="#EF3340" />
        </>
      )}
      {flag === 'netherlands' && (
        <>
          <rect width="60" height="20" fill="#AE1C28" />
          <rect y="20" width="60" height="20" fill="#fff" />
          <rect y="40" width="60" height="20" fill="#21468B" />
        </>
      )}
    </svg>
  );
}

/**
 * Glossy enamel national-flag tile for leagues and races. Each competition carries
 * its host country (UCI rainbow for Worlds, Noto globe for the World Cup).
 */
export function NationalFlagEmblem({
  leagueId,
  country,
  slug,
  flag: flagProp,
  size = 'md',
  muted = false,
  className
}: {
  leagueId?: string;
  country?: string | null;
  slug?: string | null;
  /** Override the resolved flag (e.g. showcase pages). */
  flag?: FlagEmblemKey;
  size?: Size;
  muted?: boolean;
  className?: string;
}) {
  const flag = flagProp ?? (leagueId ? flagEmblemKey(leagueId, country, slug) : 'world');
  const d = DIM[size];

  return (
    <div
      className={cn('relative shrink-0 overflow-hidden', muted && 'opacity-80 saturate-[0.85]', className)}
      style={{
        width: d.box,
        height: d.box,
        borderRadius: d.radius,
        boxShadow: '0 6px 14px -6px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.25)'
      }}
      aria-hidden
    >
      <FlagArt flag={flag} box={d.box} />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          borderRadius: d.radius,
          background: 'linear-gradient(155deg, rgba(255,255,255,0.4), rgba(255,255,255,0) 45%, rgba(0,0,0,0.14))'
        }}
      />
    </div>
  );
}
